import { AfterContentInit, Component, ElementRef, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { environment } from 'environments/environment';
import { AuthService } from 'app/user-auth/services/auth.service';
import { ActivatedRoute } from '@angular/router';
import { finalize, map, Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-log-in',
  templateUrl: './log-in.component.html',
  styleUrls: ['./log-in.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class LogInComponent implements AfterContentInit, OnDestroy {
  @ViewChild('userEmail') userEmail?: ElementRef<HTMLInputElement>;

  email = '';
  loginMessage?: string;
  rememberMe: boolean;
  private redirectUrl?: string;
  private organization?: string;

  message?: SafeHtml;
  isSubmitting = false;
  private focused = false;
  private routeDataSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute
  ) {
    const queryParams = parseQueryString(window.location.search.substr(1));

    this.rememberMe = localStorage.getItem('rememberMe') !== 'false';
    this.email = queryParams['userEmail'] || localStorage.getItem('email') || '';
    this.redirectUrl = queryParams['redirectUrl'] || queryParams['redirect_url'];
    this.routeDataSubscription = this.activatedRoute.data.subscribe(routeData => {
      this.loginMessage = routeData['loginMessage'];
      this.organization = queryParams['tenantId'] || routeData['organization'];
    });

    const connection = queryParams['connection'];
    const samlResponse = queryParams['samlResponse'];
    if (connection && samlResponse) {
      authService.postSamlResponse(connection, samlResponse);
    }

    const ttCompanyId = queryParams['tt_company_id'];
    if (connection && ttCompanyId) {
      this.isSubmitting = true;
      this.resolveTtCompanyId(connection, ttCompanyId)
        .pipe(
          map(response => this.processTtResponse(connection, response)),
          finalize(() => this.isSubmitting = false))
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.routeDataSubscription.unsubscribe();
  }

  isTextTyped(): boolean {
    return !!this.email && this.email.length > 0;
  }

  onChangeFocus(focus: boolean): void {
    this.focused = focus;
  }

  isValidInput(): boolean {
    return !!this.userEmail && this.userEmail.nativeElement.validity.valid;
  }

  ngAfterContentInit(): void {
    if (this.userEmail) {
      this.userEmail.nativeElement.focus();
    }
  }

  labelOpacity(): number {
    if (this.isTextTyped()) {
      return 0;
    }
    if (this.focused) {
      return 0.5;
    }
    return 1;
  }

  submit(form: { value: { email: string } }): void {
    if (!this.isValidInput()) {
      return;
    }

    if (this.rememberMe) {
      localStorage.setItem('email', form.value.email);
    } else {
      localStorage.removeItem('email');
    }
    localStorage.setItem('rememberMe', `${this.rememberMe}`);

    this.isSubmitting = true;
    this.http.post(environment.loginUrl, {
      email: form.value.email,
      redirect: this.redirectUrl,
      organization: this.organization
    }).subscribe((data: any) => {

      if (!data.errorMessage) {
        this.message = this.sanitizer.bypassSecurityTrustHtml(data.message);

        if (isSamlLogin(data)) {
          this.authService.redirectToProvider(data['connection'], data['clientId']);
        }
      }

      this.isSubmitting = false;
    });
  }

  private resolveTtCompanyId(connection: string, ttCompanyId: string): Observable<LoginResponse> {
    const body = { ttCompanyId: ttCompanyId, connection: connection };
    return this.http.post<LoginResponse>(environment.loginUrl, body);
  }

  private processTtResponse(connection: string, response: LoginResponse): void {
    if (response.message) {
      this.message = this.sanitizer.bypassSecurityTrustHtml(response.message);
    }
    if (response.clientId) {
      this.authService.redirectToProvider(connection, response.clientId);
    }
  }
}

function isSamlLogin(data: any): boolean {
  return 'connection' in data && 'clientId' in data;
}

function parseQueryString(query: string): { [key: string]: string } {
  const vars = query.split('&');
  const queryString: { [key: string]: any } = {};
  for (const param of vars) {
    const pair = param.split('=');
    if (typeof queryString[pair[0]] === 'undefined') {
      queryString[pair[0]] = decodeURIComponent(pair[1]);
    } else if (typeof queryString[pair[0]] === 'string') {
      queryString[pair[0]] = [queryString[pair[0]], decodeURIComponent(pair[1])];
    } else {
      queryString[pair[0]].push(decodeURIComponent(pair[1]));
    }
  }
  return queryString;
}

interface LoginResponse {
  message: string
  connection?: string
  clientId?: string
}
